<template>
    <b-button
        class="white-icon-button"
        :class="dynamicBtnClasses"
        @click="eventEmitter"
        :ref="id"
        :disabled="disabled"
        :href="href"
    >
        <b-tooltip v-if="tooltip" triggers="hover" :target="() => $refs[id]" :title="tooltip" :placement="tooltipPlacement"></b-tooltip>
        <template v-if="isBootstrapIcon">
            <b-icon
                v-if="icon"
                :icon="icon"
                class="i-button-icon"
            ></b-icon>
        </template>
        <template v-else>
            <SvgIcon
                v-if="icon"
                :name="icon"
                :width="size === 'small' ? minSize : maxSize"
                :height="size === 'small' ? minSize : maxSize"
            />
        </template>
        <span
            v-if="text"
            class="i-button-text"
            :class="[{'ml-2': (!hideTextMobile && icon)}, {'ml-0 ml-sm-2 d-none d-md-inline-block': hideTextMobile}]"
        >{{ text }}</span>
    </b-button>
</template>

<script>
    import {BIcon} from 'bootstrap-vue';
    import SvgIcon from '../shared/SvgIcon';
    import {getKey} from "../../utils/common";
    /**
     * Custom wrapper for button. Can have an icon
     * and optional / hide-able text on mobile.
     * Has active / inactive status.
     * Can be referenced with ref as to be used by
     * the parent.
     */

    export default {
        name: "WhiteIconButton",
        components: {
            BIcon,
            SvgIcon
        },
        props: {
            icon: {
                type: String,   //b-icon svg name
                required: false
            },
            text: {
                type: String,   // btn text
                required: false
            },
            hideTextMobile: {
                type: Boolean,  // hide text on small screens
                default: false
            },
            emitThis: {
                type: String,   // emit event, handled then in parent, no func passes
                required: false
            },
            tooltip: {
                type: String,   // show tooltip
                default: ''
            },
            tooltipPlacement: {
                type: String,
                default: 'top'
            },
            active: {
                type: Boolean,  // is it active (purple), css class
                default: false
            },
            disabled: {
                type: Boolean,  // if disabled (loading)
                default: false
            },
            isBootstrapIcon: {
                type: Boolean,
                default: false
            },
            size: {
                type: String,   // large or small
                default: "large"
            },
            href: {
                type: String,
                default: null
            },
            customSpacing: {
                type: String,
                default: "mx-1 mx-sm-2 my-2 b-0 py-2"
            }
        },
        data() {
            return {
                id: getKey(),
                minSize: 10,
                maxSize: 18
            }
        },
        mounted () {
            this.id = this._uid;
        },
        computed: {
            dynamicBtnClasses() {
                let classes = [];

                // icon class
                 if (!this.text) {
                     classes.push('only-icon');
                 }
                 else if (this.hideTextMobile) {
                     classes.push('only-icon-mobile');
                 }

                 // is it active and purple?
                if (this.active) {
                    classes.push('active-purple');
                }

                // is it small or large
                if (this.size === 'large') {
                    classes.push('d-large-btn');
                }
                else if (this.size === 'small') {
                    classes.push('d-small-btn');
                }

                // spacing utilities override
                classes.push(this.customSpacing);

                return classes;
            }
        },
        methods: {
            eventEmitter() {
                if (this.emitThis) {
                    this.$emit(this.emitThis);
                }

            }
        }
    }
</script>

<style lang="scss" scoped>
    @import "src/assets/scss/variables";

    .white-icon-button {
        background: $whiteColor 0% 0% no-repeat padding-box;
        box-shadow: 0px 2px 6px $darkBlueShadow;
        border-radius: 5px;
        border-color: $lightGrey;
        opacity: 1;
        // flex
        display: flex;
        justify-content: center;
        align-items: center;
        color: $blackColor;
        &:active {
            background-color: $lightGrey !important;
            border-color: $lightGrey !important;
            &:focus {
                box-shadow: none !important;
                outline: none !important;
            }
        }
        &:hover {
            outline: 0;
            box-shadow: 0 3px 7px 0 $darkBlueShadow, 0 3px 7px 0 $darkBlueShadow;
        }
        &.only-icon {
            padding: 0;
            &.d-large-btn {
                width: 36px;
                height: 36px;
            }
            &.d-small-btn {
                width: 27px;
                height: 27px;
            }
        }

        .i-button-icon {
            color: $purpleColor !important;
            opacity: 1;
        }
        .i-button-text {
            text-align: left;
            font: normal normal normal 14px/14px Nunito, sans-serif;
            letter-spacing: 0px;
            color: $blackColor;
            opacity: 1;
        }
        /* Set active class by reversing colors, need to override previous styles */
        &.active-purple {
            background-color: $purpleColor;
            border-color: $purpleColor;
            .i-button-icon {
                color: $whiteColor !important;
            }
            .i-button-text {
                color: $whiteColor !important;
            }

            svg {
                filter: brightness(2);
            }
        }

        &.disabled {
            background-color: $white-icon-button-disabled-bg-color;
            box-shadow: none;
            color: $white-icon-button-disabled-color;

            &:hover {
                box-shadow: none;
            }
        }
    }

    /*@media screen and (max-width: 762px) {*/
    /*    .white-icon-button {*/
    /*        &.only-icon-mobile {*/
    /*            padding: 0;*/
    /*            min-width: 36px;*/
    /*            min-height: 36px;*/
    /*        }*/
    /*    }*/
    /*}*/
</style>